import { useCallback, useMemo } from 'react';
import { useFile, useSelection, useUI } from '../context/AppContext.jsx';
import { pluginRegistry } from '../plugins/PluginRegistry.js';
import { getWaypointIndicesToMirror } from '../services/MirrorService.js';

/**
 * Hook for accessing and using the plugin system
 * Provides functions to apply transforms and run validators
 */
export function usePlugins() {
  const {
    currentWaypoints,
    setCurrentWaypoints,
    currentFeatures,
    nodes,
    parser,
  } = useFile();
  const { multiSelectedIndices } = useSelection();
  const { showStatus } = useUI();

  // Get available transforms
  const transforms = useMemo(() => {
    return pluginRegistry.getAllTransforms();
  }, []);

  // Get available validators
  const validators = useMemo(() => {
    return pluginRegistry.getAllValidators();
  }, []);

  // Get transforms by category
  const getTransformsByCategory = useCallback((category) => {
    return pluginRegistry.getTransformsByCategory(category);
  }, []);

  /**
   * Apply a transform to waypoints
   * @param {string} transformId - ID of the transform to apply
   * @param {Object} options - Transform options
   * @param {string} scope - 'all', 'selected', or 'node'
   * @param {number} nodeIndex - Node index (for 'node' scope)
   * @returns {boolean} Success status
   */
  const applyTransform = useCallback((transformId, options = {}, scope = 'all', nodeIndex = null) => {
    const transform = pluginRegistry.getTransform(transformId);
    if (!transform) {
      showStatus(`Transform "${transformId}" not found`, 'error');
      return false;
    }

    try {
      // Get indices based on scope
      const { indices, error } = getWaypointIndicesToMirror(
        scope,
        currentWaypoints,
        multiSelectedIndices,
        nodes,
        nodeIndex
      );

      if (error) {
        showStatus(error, 'error');
        return false;
      }

      if (indices.length === 0) {
        showStatus('No waypoints to transform', 'warning');
        return false;
      }

      // Apply transform with indices option
      const transformedWaypoints = pluginRegistry.applyTransform(
        transformId,
        currentWaypoints,
        { ...options, indices }
      );

      // Update parser for each changed waypoint
      indices.forEach((index) => {
        const waypoint = transformedWaypoints[index];
        if (waypoint.position) {
          parser.updateWaypointPosition(index, waypoint.position);
        }
        if (waypoint.rotation) {
          parser.updateWaypointRotation(index, waypoint.rotation);
        }
      });

      // Update state
      setCurrentWaypoints(transformedWaypoints);
      
      showStatus(`Applied "${transform.name}" to ${indices.length} waypoint(s)`, 'success');
      return true;
    } catch (error) {
      console.error('Error applying transform:', error);
      showStatus(`Error applying transform: ${error.message}`, 'error');
      return false;
    }
  }, [currentWaypoints, setCurrentWaypoints, multiSelectedIndices, nodes, parser, showStatus]);

  /**
   * Run a specific validator
   * @param {string} validatorId - ID of the validator to run
   * @returns {{valid: boolean, messages: Array<string>}} Validation result
   */
  const runValidator = useCallback((validatorId) => {
    const validator = pluginRegistry.getValidator(validatorId);
    if (!validator) {
      return { valid: false, messages: [`Validator "${validatorId}" not found`] };
    }

    try {
      return pluginRegistry.runValidator(validatorId, {
        waypoints: currentWaypoints,
        features: currentFeatures,
      });
    } catch (error) {
      console.error('Error running validator:', error);
      return { valid: false, messages: [error.message] };
    }
  }, [currentWaypoints, currentFeatures]);

  /**
   * Run all registered validators
   * @returns {{valid: boolean, results: Array<{id: string, name: string, valid: boolean, messages: Array<string>}>}}
   */
  const runAllValidators = useCallback(() => {
    const results = pluginRegistry.runAllValidators({
      waypoints: currentWaypoints,
      features: currentFeatures,
    });

    const allValid = results.every(r => r.valid);
    const errorCount = results.reduce((count, r) => count + (r.valid ? 0 : r.messages.length), 0);

    if (allValid) {
      showStatus('All validations passed', 'success');
    } else {
      showStatus(`Validation found ${errorCount} issue(s)`, 'warning');
    }

    return { valid: allValid, results };
  }, [currentWaypoints, currentFeatures, showStatus]);

  /**
   * Run all validators with additional context (e.g., robot parameters)
   * @param {Object} context - Additional context to pass to validators
   * @param {string} context.robotType - Robot type string
   * @param {number} context.robotReach - Robot reach in meters
   * @param {string} context.robotName - Robot name
   * @returns {{valid: boolean, results: Array}}
   */
  const runAllValidatorsWithContext = useCallback((context = {}) => {
    const results = pluginRegistry.runAllValidators({
      waypoints: currentWaypoints,
      features: currentFeatures,
      ...context,
    });

    const allValid = results.every(r => r.valid);
    const errorCount = results.reduce((count, r) => count + (r.valid ? 0 : r.messages.length), 0);

    if (allValid) {
      showStatus(`All validations passed for ${context.robotName || 'robot'}`, 'success');
    } else {
      showStatus(`Validation found ${errorCount} issue(s) for ${context.robotName || 'robot'}`, 'warning');
    }

    return { valid: allValid, results };
  }, [currentWaypoints, currentFeatures, showStatus]);

  /**
   * Validate before export - convenience method
   * @returns {boolean} True if valid and safe to export
   */
  const validateForExport = useCallback(() => {
    const { valid, results } = runAllValidators();
    
    if (!valid) {
      // Log detailed issues
      results.forEach(r => {
        if (!r.valid) {
          console.warn(`[${r.name}]`, r.messages);
        }
      });
    }
    
    return valid;
  }, [runAllValidators]);

  return {
    // Available plugins
    transforms,
    validators,
    getTransformsByCategory,
    
    // Transform operations
    applyTransform,
    
    // Validation operations
    runValidator,
    runAllValidators,
    runAllValidatorsWithContext,
    validateForExport,
  };
}
